import React, { useEffect, useState, useRef } from "react";
import { Modal, Nav, Tab, Button } from "react-bootstrap";
import { useAuth } from "../../../../firebase/AuthProvider";
import moment from "moment";
import {
    addUpdateTimeOff,
    getLeaveTypes,
} from "../../../../services/userServices/UserInfoService";
import { UpdateLeaveRequestTypeDto } from "../../../../interfaces/Employee/Employee";
import { getUpcomingTimeOffs } from "../../../../services/EmployeeService/EmployeeService";
import { TimeOffPayloadBackend, TimeOffRequestFrontend } from "../../../../services/AdministrativeRequestServices/AdministrativeRequestService";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle, faInfoCircle, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { CgClose } from "react-icons/cg";
import DropdownNew, { DropdownOption } from "../../TasksPage/Dropdown";
import Input from "../../TasksPage/Input";
import CalendarIcon from "../../../../assets/images/DateGray.svg";
import { getTasks } from "../../../../services/taskServices/TaskService";
import TaskList from "../../../../interfaces/task/TaskList";
import TaskFilters from "../../../../interfaces/task/TaskFilters";
import { FiChevronDown } from "react-icons/fi";
import "./AdminRequestModal.css";

const ChevronDown = FiChevronDown as any;

interface AdminRequestModalProps {
    show: boolean;
    handleClose: () => void;
    onTaskSelected?: (taskId: string, requestType: string) => void;
}

interface GeneratedDay {
    date: string;
    isFullDay: boolean;
}

const AdminRequestModal: React.FC<AdminRequestModalProps> = ({
    show,
    handleClose,
    onTaskSelected,
}) => {
    const { currentUser, currentOrganization } = useAuth();
    const [activeTab, setActiveTab] = useState("time-off");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [leaveTypes, setLeaveTypes] = useState<UpdateLeaveRequestTypeDto[]>([]);
    const [generatedDays, setGeneratedDays] = useState<GeneratedDay[]>([]);
    const [totalHours, setTotalHours] = useState<number>(0);
    const [projectedBalance, setProjectedBalance] = useState<number>(0);
    const [remainingLeaveCount, setRemainingLeaveCount] = useState<number>(0);
    const [holidays, setHolidays] = useState<string[]>([]);

    const [formData, setFormData] = useState<TimeOffRequestFrontend>({
        userId: "",
        timeOffStartTime: "",
        timeOffEndTime: "",
        typeOfLeave: 0,
        isApproved: false,
        isRejected: false,
        comment: "",
        reason: "",
    });

    const [errors, setErrors] = useState<{
        typeOfLeave?: string;
        startDate?: string;
        endDate?: string;
    }>({});

    // Other Requests form state
    const [otherRequestFormData, setOtherRequestFormData] = useState({
        requestName: "",
        type: null as DropdownOption | null,
        sendTo: null as DropdownOption | null,
        description: "",
        attachedFiles: [] as File[],
        requestedDocuments: [] as string[],
        expirationDate: "",
        selectedTask: null as TaskList | null,
    });

    const [otherRequestErrors, setOtherRequestErrors] = useState<{
        requestName?: string;
        type?: string;
        sendTo?: string;
        expirationDate?: string;
        selectedTask?: string;
    }>({});

    // Task search state
    const [taskSearchTerm, setTaskSearchTerm] = useState("");
    const [taskOptions, setTaskOptions] = useState<TaskList[]>([]);
    const [isTaskDropdownOpen, setIsTaskDropdownOpen] = useState(false);
    const [isSearchingTasks, setIsSearchingTasks] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [taskCurrentPage, setTaskCurrentPage] = useState(1);
    const [taskTotalCount, setTaskTotalCount] = useState(0);
    const taskDropdownRef = useRef<HTMLDivElement>(null);

    // Mock data for transaction types and recipients (replace with actual API calls)
    const requestTypeOptions: DropdownOption[] = [
        { value: "task-deletion-request", label: "Task Deletion Request" },
        { value: "task-extension-request", label: "Task Extension Request" },
        // { value: "task-reactivation-request", label: "Task Reactivation Request" }
    ];

    const recipientOptions: DropdownOption[] = [
        { value: "admin", label: "Administrator" },
        { value: "supervisor", label: "Supervisor" },
        { value: "manager", label: "Manager" },
    ];

    const Close = CgClose as React.ComponentType;

    useEffect(() => {
        if (show) {
            fetchLeaveTypes();
            fetchTimeOffs();
            resetForm();
        }
    }, [show, currentUser]);

    const fetchLeaveTypes = async () => {
        try {
            if (currentUser) {
                const authToken = await currentUser.getIdToken();
                const response = await getLeaveTypes(authToken);
                if (!("error" in response)) {
                    setLeaveTypes(response);
                }
            }
        } catch (err) {
            console.error("Error fetching leave types:", err);
        }
    };

    const fetchTimeOffs = async () => {
        if (currentUser) {
            try {
                const authToken = await currentUser.getIdToken();
                const userId = currentUser.uid;
                const result = await getUpcomingTimeOffs(userId, authToken);
                if (result.success) {
                    setRemainingLeaveCount(result.data[0]?.remainingLeaveCount || 0);
                }
            } catch (error: any) {
                console.error("Error fetching time offs:", error);
            }
        }
    };

    const resetForm = () => {
        setFormData({
            userId: "",
            timeOffStartTime: "",
            timeOffEndTime: "",
            typeOfLeave: 0,
            isApproved: false,
            isRejected: false,
            comment: "",
            reason: "",
        });
        setGeneratedDays([]);
        setTotalHours(0);
        setErrors({});
        setHolidays([]);

        // Reset other requests form
        setOtherRequestFormData({
            requestName: "",
            type: null,
            sendTo: null,
            description: "",
            attachedFiles: [],
            requestedDocuments: [],
            expirationDate: "",
            selectedTask: null,
        });
        setOtherRequestErrors({});
        setTaskSearchTerm("");
        setTaskOptions([]);
        setIsTaskDropdownOpen(false);
        setTaskCurrentPage(1);
        setTaskTotalCount(0);
    };

    const handleTabSelect = (selectedKey: any) => {
        setActiveTab(selectedKey);
        resetForm();
    };

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear errors when user starts typing
        if (errors[name as keyof typeof errors]) {
            setErrors((prev) => ({
                ...prev,
                [name]: undefined,
            }));
        }
    };

    const handleLeaveTypeChange = (value: string | number) => {
        setFormData((prev) => ({
            ...prev,
            typeOfLeave: value ? Number(value) : 0,
        }));
        if (errors.typeOfLeave) {
            setErrors((prev) => ({
                ...prev,
                typeOfLeave: undefined,
            }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: typeof errors = {};

        if (!formData.typeOfLeave) {
            newErrors.typeOfLeave = "Type of Leave is required";
        }
        if (!formData.timeOffStartTime) {
            newErrors.startDate = "Start Date is required";
        }
        if (!formData.timeOffEndTime) {
            newErrors.endDate = "End Date is required";
        }

        const parseLocal = (dateStr: string) => {
            // Splits '2026-01-22' into [2026, 01, 22]
            const [year, month, day] = dateStr.split('-').map(Number);
            // new Date(year, monthIndex, day) creates a date in LOCAL time
            return new Date(year, month - 1, day);
        };

        if (formData.timeOffStartTime && formData.timeOffEndTime) {
            const start = parseLocal(formData.timeOffStartTime);
            const end = parseLocal(formData.timeOffEndTime);

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (start < today) {
                newErrors.startDate = "Start date must be today or in the future.";
            }
            if (start > end) {
                newErrors.endDate = "Start date cannot be after end date.";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const generateDays = () => {
        if (!formData.timeOffStartTime || !formData.timeOffEndTime) {
            toast.error("Please select both start and end dates");
            return;
        }

        const start = new Date(formData.timeOffStartTime);
        const end = new Date(formData.timeOffEndTime);

        if (end < start) {
            toast.error("End date must be greater than or equal to start date");
            return;
        }

        const days: GeneratedDay[] = [];
        const detectedHolidays: string[] = [];
        const current = new Date(start);

        while (current <= end) {
            const dateStr = current.toISOString().split("T")[0];
            // Check if it's a weekend (you can add holiday logic here)
            const dayOfWeek = current.getDay();
            if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                days.push({ date: dateStr, isFullDay: true });
            } else {
                detectedHolidays.push(dateStr);
            }
            current.setDate(current.getDate() + 1);
        }

        setGeneratedDays(days);
        setHolidays(detectedHolidays);
        setTotalHours(days.length * 8); // Assuming 8 hours per day
        setProjectedBalance(remainingLeaveCount - days.length);
    };

    const handleDayToggle = (index: number) => {
        const updatedDays = [...generatedDays];
        updatedDays[index].isFullDay = !updatedDays[index].isFullDay;
        setGeneratedDays(updatedDays);

        const fullDays = updatedDays.filter(d => d.isFullDay).length;
        setTotalHours(fullDays * 8);
        setProjectedBalance(remainingLeaveCount - fullDays);
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        // if (generatedDays.length === 0) {
        //     toast.error("Please generate days first");
        //     return;
        // }

        setIsSubmitting(true);

        if (currentUser) {
            const authToken = await currentUser.getIdToken();
            const currentUserId = (currentUser as any)?.uid;

            const startTimeISO = `${formData.timeOffStartTime}T00:00:00.000Z`;
            const endTimeISO = `${formData.timeOffEndTime}T23:59:59.999Z`;

            const payload: TimeOffPayloadBackend = {
                id: 0,
                userId: currentUserId,
                typeOfLeave: formData.typeOfLeave || 0,
                comment: formData.comment,
                isApproved: false,
                timeOffStartTime: startTimeISO,
                timeOffEndTime: endTimeISO,
            };

            try {
                const response = await addUpdateTimeOff(payload, authToken);

                if (!response.success) {
                    toast.error(response.message || "Failed to submit request");
                } else {
                    toast.success(response.message || "Request submitted successfully!");
                    setTimeout(() => {
                        handleClose();
                    }, 500);
                }
            } catch (error: any) {
                console.error("Failed to submit request:", error);
                toast.error(error.message || "An unexpected error occurred. Please try again.");
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const leaveTypeOptions: DropdownOption[] = leaveTypes
        .filter((t: any) => t.isActive && !t.isDeleted)
        .map((t: any) => ({ value: t.id, label: t.requestType }));

    const isFormValid = formData.typeOfLeave > 0 &&
        formData.timeOffStartTime &&
        formData.timeOffEndTime
    // && generatedDays.length > 0;

    // Other Requests form handlers
    const handleOtherRequestInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setOtherRequestFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear errors when user starts typing
        if (otherRequestErrors[name as keyof typeof otherRequestErrors]) {
            setOtherRequestErrors((prev) => ({
                ...prev,
                [name]: undefined,
            }));
        }
    };

    const handleTypeChange = (value: string | number) => {
        const selectedOption = requestTypeOptions.find(opt => opt.value === value);
        // Don't allow selection of disabled options
        // if (selectedOption?.disabled) {
        //     return;
        // }
        setOtherRequestFormData((prev) => ({
            ...prev,
            type: selectedOption || null,
            selectedTask: null, // Reset task when type changes
        }));
        setTaskSearchTerm("");
        setTaskOptions([]);
        setTaskCurrentPage(1);
        setTaskTotalCount(0);
        if (otherRequestErrors.type) {
            setOtherRequestErrors((prev) => ({
                ...prev,
                type: undefined,
            }));
        }
    };

    // Fetch tasks function (reusable for initial load and load more)
    const fetchTasks = React.useCallback(async (page: number, append: boolean = false, searchTerm?: string, selectedType?: DropdownOption) => {
        if (!selectedType || !currentUser) {
            return;
        }

        const term = searchTerm !== undefined ? searchTerm : taskSearchTerm.trim();

        try {
            if (append) {
                setIsLoadingMore(true);
            } else {
                setIsSearchingTasks(true);
            }

            const authToken = await currentUser.getIdToken();

            // Build filters
            const filters: TaskFilters = {};

            // Add search term if provided
            if (term) {
                filters.searchTerm = term;
            }

            // If type is "Task Extension Request", add date filter
            if (selectedType?.value === "task-extension-request") {
                filters.date = ["overdue", "today"];
            }

            // If type is "Task Deletion Request", exclude COMPLETED and DELETED statuses
            if (selectedType?.value === "task-deletion-request") {
                filters.excludeStatus = ["COMPLETED", "DELETED"];
            }

            const result = await getTasks(
                authToken,
                undefined, // userId
                page,
                100, // pageSize - limit to 100 for dropdown
                filters,
                "", // sortBy
                false, // sortDesc
                false, // viewAll
                false, // includePendingApprovals
                term || undefined // searchTerm parameter
            );

            if (result) {
                if (append) {
                    setTaskOptions((prev) => [...prev, ...result.tasks]);
                } else {
                    setTaskOptions(result.tasks);
                }
                setTaskTotalCount(result.totalCount);
                setTaskCurrentPage(page);
            } else {
                if (!append) {
                    setTaskOptions([]);
                }
            }
        } catch (error) {
            console.error("Error searching tasks:", error);
            if (!append) {
                setTaskOptions([]);
            }
        } finally {
            setIsSearchingTasks(false);
            setIsLoadingMore(false);
        }
    }, [currentUser, taskSearchTerm]);

    // Task search with debounce - fetch initial tasks when type is selected, or search when typing
    useEffect(() => {
        if (!otherRequestFormData.type || !currentUser) {
            setTaskOptions([]);
            setTaskTotalCount(0);
            setTaskCurrentPage(1);
            return;
        }

        const selectedType = otherRequestFormData.type;
        const searchTerm = taskSearchTerm.trim();

        // If search term is empty, fetch immediately (initial load)
        // If search term exists, debounce the search
        const delay = searchTerm === "" ? 0 : 400;

        const debounceTimer = setTimeout(() => {
            fetchTasks(1, false, searchTerm, selectedType);
        }, delay);

        return () => clearTimeout(debounceTimer);
    }, [taskSearchTerm, otherRequestFormData.type, currentUser, fetchTasks]);

    // Load more handler
    const handleLoadMore = React.useCallback(() => {
        if (otherRequestFormData.type) {
            const nextPage = taskCurrentPage + 1;
            fetchTasks(nextPage, true, taskSearchTerm.trim(), otherRequestFormData.type);
        }
    }, [taskCurrentPage, taskSearchTerm, otherRequestFormData.type, fetchTasks]);

    // Close task dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (taskDropdownRef.current && !taskDropdownRef.current.contains(event.target as Node)) {
                setIsTaskDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Helper function to get due date status for a task
    const getTaskDueDateStatus = (task: TaskList) => {
        if (!task || ["COMPLETED", "CLOSED"].includes(task.status.name)) {
            return { isLate: false, isAtRisk: false };
        }
        const lateRule = currentOrganization?.atRiskWindowDays ?? 0;
        const daysUntilDue = moment(task.endDate)
            .startOf("day")
            .diff(moment().startOf("day"), "days");
        if (daysUntilDue < 0) {
            return { isLate: true, isAtRisk: false };
        }
        if (lateRule > 0 && daysUntilDue <= lateRule) {
            return { isLate: false, isAtRisk: true };
        }
        return { isLate: false, isAtRisk: false };
    };

    const handleTaskSelect = (task: TaskList) => {
        setOtherRequestFormData((prev) => ({
            ...prev,
            selectedTask: task,
        }));
        setTaskSearchTerm(task.title);
        setIsTaskDropdownOpen(false);
        if (otherRequestErrors.selectedTask) {
            setOtherRequestErrors((prev) => ({
                ...prev,
                selectedTask: undefined,
            }));
        }
    };

    const handleSendToChange = (value: string | number) => {
        const selectedOption = recipientOptions.find(opt => opt.value === value);
        setOtherRequestFormData((prev) => ({
            ...prev,
            sendTo: selectedOption || null,
        }));
        if (otherRequestErrors.sendTo) {
            setOtherRequestErrors((prev) => ({
                ...prev,
                sendTo: undefined,
            }));
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const filesArray = Array.from(e.target.files);
            setOtherRequestFormData((prev) => ({
                ...prev,
                attachedFiles: [...prev.attachedFiles, ...filesArray],
            }));
        }
    };

    const handleRemoveFile = (index: number) => {
        setOtherRequestFormData((prev) => ({
            ...prev,
            attachedFiles: prev.attachedFiles.filter((_, i) => i !== index),
        }));
    };

    const handleAddRequestedDocument = () => {
        const documentName = prompt("Enter document name:");
        if (documentName && documentName.trim()) {
            setOtherRequestFormData((prev) => ({
                ...prev,
                requestedDocuments: [...prev.requestedDocuments, documentName.trim()],
            }));
        }
    };

    const handleRemoveRequestedDocument = (index: number) => {
        setOtherRequestFormData((prev) => ({
            ...prev,
            requestedDocuments: prev.requestedDocuments.filter((_, i) => i !== index),
        }));
    };

    const validateOtherRequestForm = (): boolean => {
        const newErrors: typeof otherRequestErrors = {};

        // Only validate type and selected task
        if (!otherRequestFormData.type) {
            newErrors.type = "Type is required";
        }
        if (!otherRequestFormData.selectedTask) {
            newErrors.selectedTask = "Task selection is required";
        }

        // Commented out other validations for now
        // if (!otherRequestFormData.requestName.trim()) {
        //     newErrors.requestName = "Request Name is required";
        // }
        // if (!otherRequestFormData.sendTo) {
        //     newErrors.sendTo = "Send to is required";
        // }
        // if (!otherRequestFormData.expirationDate) {
        //     newErrors.expirationDate = "Expiration Date is required";
        // }

        setOtherRequestErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleOtherRequestSubmit = async () => {
        if (!validateOtherRequestForm()) {
            return;
        }

        // Check if task is selected and request type is task-deletion-request or task-extension-request
        if (otherRequestFormData.selectedTask && otherRequestFormData.type) {
            const requestType = otherRequestFormData.type.value as string;
            if (requestType === "task-deletion-request" || requestType === "task-extension-request") {
                // Call the callback to open the appropriate modal
                if (onTaskSelected) {
                    onTaskSelected(otherRequestFormData.selectedTask.id, requestType);
                }
                return; // Don't proceed with normal submission
            }
        }

        setIsSubmitting(true);

        if (currentUser) {
            try {
                const authToken = await currentUser.getIdToken();
                // TODO: Implement API call to create other request
                // const payload = {
                //     requestName: otherRequestFormData.requestName,
                //     type: otherRequestFormData.type?.value,
                //     sendTo: otherRequestFormData.sendTo?.value,
                //     description: otherRequestFormData.description,
                //     expirationDate: otherRequestFormData.expirationDate,
                //     attachedFiles: otherRequestFormData.attachedFiles,
                //     requestedDocuments: otherRequestFormData.requestedDocuments,
                // };

                // Placeholder for now
                toast.success("Request submitted successfully!");
                handleClose();
            } catch (error: any) {
                console.error("Failed to submit request:", error);
                toast.error(error.message || "An unexpected error occurred");
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const isOtherRequestFormValid =
        otherRequestFormData.type &&
        otherRequestFormData.selectedTask;
    // Commented out other validations for now
    // otherRequestFormData.requestName.trim() &&
    // otherRequestFormData.sendTo &&
    // otherRequestFormData.expirationDate &&

    // Check if vacation request is at least 2 weeks in advance
    const isTwoWeeksAdvance = () => {
        if (!formData.timeOffStartTime) return true;
        const start = new Date(formData.timeOffStartTime);
        const today = new Date();
        const diffTime = start.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays >= 14;
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            centered
            className="w-[512px]"
        >
            <Modal.Header className="border-0 p-0">
                <div className="flex items-center justify-between w-full">
                    <h2 className="text-xl font-semibold text-gray-900">Admin Request</h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                        aria-label="Close"
                    >
                        <Close />
                    </button>
                </div>
            </Modal.Header>

            <Modal.Body className="px-0">
                <Tab.Container activeKey={activeTab} onSelect={handleTabSelect}>
                    <div className="mb-6 modal-items">
                        <Nav variant="pills" className="common_tab">
                            <Nav.Item>
                                <Nav.Link eventKey="time-off">
                                    Time-Off
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="other-requests">
                                    Other Requests
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                    </div>
                </Tab.Container>
                {activeTab === "time-off" && (
                    <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                        <div className="space-y-5">
                            {/* Type of Leave */}
                            <DropdownNew
                                label="Type of Leave"
                                name="typeOfLeave"
                                value={formData.typeOfLeave}
                                onChange={handleLeaveTypeChange}
                                options={leaveTypeOptions}
                                placeholder="Select Leave Type"
                                required
                                error={errors.typeOfLeave}
                            />

                            {/* Remaining Leave Count */}
                            {remainingLeaveCount >= 0 ? (
                                <div className="text-sm text-green-600">
                                    You have {remainingLeaveCount} remaining leave{remainingLeaveCount === 1 ? "" : "s"}.
                                </div>
                            ) : (
                                <div className="text-sm text-red-600">
                                    You have exceeded your leave quota by {Math.abs(remainingLeaveCount)} day{Math.abs(remainingLeaveCount) === 1 ? "" : "s"}.
                                </div>
                            )}

                            {/* Date Selection */}
                            <div className="flex items-start gap-x-4">
                                <div className="w-1/2">
                                    <Input
                                        type="date"
                                        label="Start Date"
                                        name="timeOffStartTime"
                                        value={formData.timeOffStartTime}
                                        onChange={handleInputChange}
                                        icon={CalendarIcon}
                                        error={errors.startDate}
                                        required
                                        max={formData.timeOffEndTime || undefined}
                                    />
                                </div>
                                <div className="w-1/2">
                                    <Input
                                        type="date"
                                        label="End Date"
                                        name="timeOffEndTime"
                                        value={formData.timeOffEndTime}
                                        onChange={handleInputChange}
                                        icon={CalendarIcon}
                                        error={errors.endDate}
                                        required
                                        min={formData.timeOffStartTime || undefined}
                                    />
                                </div>
                            </div>

                            {/* Comment */}
                            <Input
                                label="Comment (Optional)"
                                type="textarea"
                                name="comment"
                                value={formData.comment ?? ""}
                                placeholder="Describe the work completed or progress made…"
                                onChange={handleInputChange}
                            />

                            {/* Generate Days Section - Commented out for now */}
                            {/* <div>
                                <div className="flex justify-center">
                                    <Button
                                        variant="outline-primary"
                                        onClick={generateDays}
                                        type="button"
                                        className="border-2 border-[#7e57c2] text-[#7e57c2] bg-transparent hover:bg-[#7e57c2] hover:text-white rounded-lg px-6 py-2"
                                    >
                                        Generate Days
                                    </Button>
                                </div>

                                {generatedDays.length > 0 && (
                                    <div className="mt-4 space-y-2 max-h-60 overflow-y-auto">
                                        {generatedDays.map((day, index) => (
                                            <div key={index} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                                                <span className="text-sm font-medium text-gray-700">{day.date}</span>
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={day.isFullDay}
                                                        onChange={() => handleDayToggle(index)}
                                                        className="w-4 h-4 text-[#7e57c2] rounded border-gray-300 focus:ring-[#7e57c2]"
                                                    />
                                                    <span className="text-sm text-gray-700">Full Day</span>
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div> */}

                            {/* Alerts - Commented out for now */}
                            {/* {formData.timeOffStartTime && !isTwoWeeksAdvance() && (
                                <div className="flex items-center gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                    <span className="text-sm">Vacation requests must be submitted at least 2 weeks in advance.</span>
                                </div>
                            )} */}

                            {/* Holiday alert - Commented out for now */}
                            {/* {holidays.length > 0 && (
                                <div className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg text-purple-700">
                                    <FontAwesomeIcon icon={faInfoCircle} />
                                    <span className="text-sm">Your request includes {holidays.length} holiday(s): {holidays.join(", ")}</span>
                                </div>
                            )} */}

                            {/* Summary Section - Commented out for now */}
                            {/* {generatedDays.length > 0 && (
                                <div className="p-4 bg-white border border-gray-200 rounded-lg space-y-2">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-medium text-gray-600">Total Hours Requested:</span>
                                        <span className="text-base font-semibold text-gray-900">{totalHours.toFixed(1)}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-medium text-gray-600">Projected Balance:</span>
                                        <span className="text-base font-semibold text-gray-900">{projectedBalance.toFixed(1)} hours</span>
                                    </div>
                                </div>
                            )} */}
                        </div>
                    </form>
                )}

                {activeTab === "other-requests" && (
                    <form onSubmit={(e) => { e.preventDefault(); handleOtherRequestSubmit(); }}>
                        <div className="space-y-5">
                            {/* Type */}
                            <DropdownNew
                                label="Type"
                                name="type"
                                value={otherRequestFormData.type?.value || ""}
                                onChange={handleTypeChange}
                                options={requestTypeOptions}
                                placeholder="Select Request Type"
                                required
                                error={otherRequestErrors.type}
                            />

                            {/* Task Selection Dropdown */}
                            {otherRequestFormData.type && (
                                <div className="relative w-full" ref={taskDropdownRef}>
                                    <label
                                        style={{
                                            display: "block",
                                            marginBottom: "8px",
                                            color: "#101828",
                                            fontSize: "14px",
                                            fontWeight: 500,
                                        }}
                                        className="!text-sm"
                                    >
                                        Task <span className="text-red-500">*</span>
                                    </label>
                                    <div
                                        onClick={() => {
                                            if (otherRequestFormData.type) {
                                                setIsTaskDropdownOpen(!isTaskDropdownOpen);
                                            }
                                        }}
                                        className={`
                                            w-full h-[48px]
                                            bg-[#F3F3F5]
                                            !rounded-[8px]
                                            px-3 pr-9
                                            flex items-center
                                            text-gray-500
                                            cursor-pointer
                                        `}
                                        style={{
                                            border: otherRequestErrors.selectedTask ? "1px solid #EF4444" : "none",
                                            fontSize: "14px",
                                        }}
                                    >
                                        {!isTaskDropdownOpen && (
                                            <span className="text-gray-600">
                                                {otherRequestFormData.selectedTask
                                                    ? otherRequestFormData.selectedTask.title
                                                    : "Search and select a task..."}
                                            </span>
                                        )}
                                        {isTaskDropdownOpen && (
                                            <input
                                                autoFocus
                                                value={taskSearchTerm}
                                                onChange={(e) => {
                                                    setTaskSearchTerm(e.target.value);
                                                }}
                                                placeholder="Search tasks..."
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    border: "none",
                                                    outline: "none",
                                                    boxShadow: "none",
                                                    background: "transparent",
                                                    fontSize: "14px",
                                                    WebkitTapHighlightColor: "transparent",
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        )}
                                        <ChevronDown
                                            size={18}
                                            className={`absolute right-3 text-gray-500 transition-transform duration-200 ${isTaskDropdownOpen ? "rotate-180" : ""
                                                }`}
                                        />
                                    </div>

                                    {/* Task Options Dropdown */}
                                    {isTaskDropdownOpen && (
                                        <ul
                                            className="
                                                absolute z-20 
                                                bg-white
                                                mt-1 rounded-md shadow-md 
                                                border border-gray-200
                                                max-h-48 w-full overflow-y-auto
                                                !p-0
                                            "
                                        >
                                            {isSearchingTasks ? (
                                                <li className="px-3 py-2 text-sm text-gray-400">Loading tasks...</li>
                                            ) : taskOptions.length === 0 ? (
                                                <li className="px-3 py-2 text-sm text-gray-400">
                                                    {taskSearchTerm.trim() === ""
                                                        ? "No tasks available"
                                                        : "No tasks found"}
                                                </li>
                                            ) : (
                                                <>
                                                    {taskOptions.map((task) => (
                                                        <li
                                                            key={task.id}
                                                            onClick={() => {
                                                                handleTaskSelect(task);
                                                            }}
                                                            className="
                                                                px-3 py-2 text-sm
                                                                cursor-pointer hover:bg-gray-100
                                                            "
                                                        >
                                                            <div className="font-medium text-gray-900">{task.title}</div>
                                                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                                                {task.participant && (
                                                                    <div className="text-xs text-gray-500">
                                                                        {task.participant.firstName} {task.participant.lastName}
                                                                    </div>
                                                                )}
                                                                {task.status && (
                                                                    <>
                                                                        {task.participant && (
                                                                            <span className="text-xs text-gray-400">•</span>
                                                                        )}
                                                                        <div className="text-xs text-gray-500">
                                                                            {task.status.name
                                                                                .toLowerCase()
                                                                                .split(" ")
                                                                                .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
                                                                                .join(" ")}
                                                                        </div>
                                                                    </>
                                                                )}
                                                                {task.endDate && (() => {
                                                                    const dueDateStatus = getTaskDueDateStatus(task);
                                                                    const dateClass = dueDateStatus.isLate || dueDateStatus.isAtRisk
                                                                        ? "text-orange-600"
                                                                        : "text-gray-500";
                                                                    const dateLabel = dueDateStatus.isLate || dueDateStatus.isAtRisk
                                                                        ? " (Due Soon)"
                                                                        : "";
                                                                    return (
                                                                        <>
                                                                            {(task.participant || task.status) && (
                                                                                <span className="text-xs text-gray-400">•</span>
                                                                            )}
                                                                            <div className={`text-xs`}>
                                                                                Due Date: <span className={`${dateClass}`}>{moment(task.endDate).format("MMM DD, YYYY")}{dateLabel}</span>
                                                                            </div>
                                                                        </>
                                                                    );
                                                                })()}
                                                            </div>
                                                        </li>
                                                    ))}
                                                    {taskOptions.length < taskTotalCount && (
                                                        <li className="border-t border-gray-200">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleLoadMore();
                                                                }}
                                                                disabled={isLoadingMore}
                                                                className="
                                                                    w-full px-3 py-2 text-sm
                                                                    text-[#7e57c2] font-medium
                                                                    hover:bg-gray-50
                                                                    disabled:opacity-50 disabled:cursor-not-allowed
                                                                    transition-colors
                                                                "
                                                            >
                                                                {isLoadingMore ? "Loading..." : "Load More"}
                                                            </button>
                                                        </li>
                                                    )}
                                                </>
                                            )}
                                        </ul>
                                    )}

                                    {/* Error message */}
                                    {otherRequestErrors.selectedTask && (
                                        <p
                                            style={{
                                                color: "#EF4444",
                                                fontSize: "13px",
                                                marginTop: "4px",
                                            }}
                                        >
                                            {otherRequestErrors.selectedTask}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Send To - Commented out for now */}
                            {/* <DropdownNew
                                label="Send To"
                                name="sendTo"
                                value={otherRequestFormData.sendTo?.value || ""}
                                onChange={handleSendToChange}
                                options={recipientOptions}
                                placeholder="Select Recipient"
                                required
                                error={otherRequestErrors.sendTo}
                            /> */}

                            {/* Expiration Date - Commented out for now */}
                            {/* <Input
                                type="date"
                                label="Expiration Date"
                                name="expirationDate"
                                value={otherRequestFormData.expirationDate}
                                onChange={handleOtherRequestInputChange}
                                icon={CalendarIcon}
                                error={otherRequestErrors.expirationDate}
                                required
                                min={new Date().toISOString().split("T")[0]}
                            /> */}

                            {/* Description - Commented out for now */}
                            {/* <Input
                                label="Description (Optional)"
                                type="textarea"
                                name="description"
                                value={otherRequestFormData.description}
                                placeholder="Enter description..."
                                onChange={handleOtherRequestInputChange}
                            /> */}
                        </div>
                    </form>
                )}
            </Modal.Body>

            <Modal.Footer className="border-0 pt-3 flex justify-end gap-2">
                <Button variant="secondary" onClick={handleClose} disabled={isSubmitting} type="button">
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={activeTab === "time-off" ? handleSubmit : handleOtherRequestSubmit}
                    disabled={
                        (activeTab === "time-off" && (!isFormValid || isSubmitting)) ||
                        (activeTab === "other-requests" && (!isOtherRequestFormValid || isSubmitting))
                    }
                    type="button"
                >
                    {isSubmitting ? "Sending..." : activeTab === "other-requests" ? "Next" : "Send Request"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AdminRequestModal;

